/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import {
  createStyles,
  Card,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Avatar,
  ActionIcon,
  Pagination,
  Checkbox,
  Badge,
  Center,
  Loader,
  Tooltip,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconSearch,
  IconChevronDown,
  IconInfoCircle,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { useGetLeavesListReport } from "api/leaveReport/getLeaveReport";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import EditLeaveModal from "features/admin-leave/components/editLeaveModal/editLeaveModal";
import DeleteApprovedLeaveModal from "features/leave-employees/components/cancelLeave/deleteApprovedLeave";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useUser } from "hooks/useUser";
import { textOverlap } from "utils/commonFunction";
import { toSafeDate } from "utils/dateGuards";

const useStyles = createStyles((theme) => ({
  page: { padding: theme.spacing.xl, width: "100%" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.md,
  },
  title: { fontSize: "18px", fontWeight: 600 },
  controls: { display: "flex", gap: theme.spacing.sm, alignItems: "center" },
  miniControls: {
    display: "flex",
    gap: theme.spacing.xs,
    alignItems: "center",
  },
  tableWrap: {
    marginTop: theme.spacing.sm,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    minHeight: 300,
  },
  table: { minWidth: 1200, whiteSpace: "nowrap" },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  empMeta: { lineHeight: 1 },
  empName: { fontWeight: 600, fontSize: "14px" },
  empRole: { fontSize: "12px", color: theme.colors.gray[6] },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing.md,
  },
  smallMuted: { fontSize: "12px", color: theme.colors.gray[6] },
}));

const formatDate = (iso: string | null | undefined) => {
  const d = toSafeDate(iso);
  return d ? d.toLocaleDateString() : "-";
};

const computeDays = (fromIso?: string | null, toIso?: string | null) => {
  const from = toSafeDate(fromIso);
  const to = toSafeDate(toIso);
  if (!from || !to) return "-";
  const fromMid = new Date(from);
  fromMid.setHours(0, 0, 0, 0);
  const toMid = new Date(to);
  toMid.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (toMid.getTime() - fromMid.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays < 0) return "0 Days";
  const count = diffDays + 1;
  return `${count} Day${count > 1 ? "s" : ""}`;
};

const statusColor = (status: string | undefined | null) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
      return "green";
    case "rejected":
    case "declined":
      return "red";
    case "pending":
      return "yellow";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
};

const formatToYYYYMMDD = (d?: Date | null) => {
  if (!d) return undefined;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

interface AdminTableLeaveReportProps {
  onFilterChange?: (filters: any) => void;
}

export default function AdminTableLeaveReport({
  onFilterChange,
}: AdminTableLeaveReportProps) {
  const { classes } = useStyles();
  const { user } = useUser();
  const { data: departmentsData, isLoading: isLoadingDepartments } =
    useGetDepartments();

  const [rowsPerPage, setRowsPerPage] = useState<string>("10");
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [status, setStatus] = useState<string>("all");
  const [leaveType, setLeaveType] = useState<string>("all");
  const [departmentId, setDepartmentId] = useState<string>("all");

  const pageSize = parseInt(rowsPerPage, 10) || 10;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const roleNormalized = (user?.role ?? "").toString();
  const isAdmin = /admin/i.test(roleNormalized);
  const isSupervisor = /supervisor/i.test(roleNormalized);
  const isMember = /member/i.test(roleNormalized);
  const disabledDept = isSupervisor || isMember;

  const departmentIdToNameMap = useMemo(() => {
    const list = departmentsData?.data ?? departmentsData ?? [];
    if (!Array.isArray(list)) return {};
    return list.reduce((acc: Record<string, string>, d: any) => {
      const id = d._id ?? d.id ?? "";
      const name = d.name ?? d.title ?? "";
      if (id) acc[String(id)] = name;
      return acc;
    }, {});
  }, [departmentsData]);

  useEffect(() => {
    if (!user) return;
    if (disabledDept && user.department?._id) {
      setDepartmentId(String(user.department._id));
    } else if (isAdmin) {
      setDepartmentId("all");
    } else {
      setDepartmentId((prev) => prev || "all");
    }
  }, [user, isAdmin, disabledDept]);

  const filters = useMemo(() => {
    const obj: any = { page, limit: pageSize };
    if (debouncedSearch) obj.search = debouncedSearch;
    if (status && status !== "all") obj.status = status;
    if (leaveType && leaveType !== "all") obj.leaveType = leaveType;
    if (departmentId && departmentId !== "all") obj.department = departmentId;
    const sd = formatToYYYYMMDD(dateRange[0]);
    const ed = formatToYYYYMMDD(dateRange[1]);
    if (sd) obj.startDate = sd;
    if (ed) obj.endDate = ed;
    return obj;
  }, [
    page,
    pageSize,
    debouncedSearch,
    status,
    leaveType,
    departmentId,
    dateRange,
  ]);

  const { data, isLoading, error, refetch } = useGetLeavesListReport(filters);
  // Row Edit/Delete state (reuses the existing leave edit/delete modals).
  const [editRow, setEditRow] = useState<any | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  useEffect(() => {
    if (onFilterChange) onFilterChange(filters);
  }, [filters, onFilterChange]);

  const apiRawList = data?.data ?? [];
  const serverPagination = data?.pagination;

  const resolveDepartmentName = (deptField: any) => {
    if (!deptField) return "-";
    if (typeof deptField === "object") return deptField.name ?? "-";
    if (typeof deptField === "string")
      return departmentIdToNameMap[deptField] ?? deptField;
    return String(deptField);
  };

  const normalizedList = useMemo(() => {
    if (!apiRawList || apiRawList.length === 0) return [];
    return apiRawList.map((item: any) => ({
      _id: item._id ?? String(Math.random()),
      employeeName: item.employeeName ?? "Unknown",
      employeeId: item.employeeId ?? item.employee_code ?? "-",
      department: resolveDepartmentName(item.department),
      designation: item.designation ?? "-",
      leaveType: item.leaveType ?? item.leave_type ?? "-",
      fromDate: item.startDate ?? item.fromDate ?? null,
      toDate: item.endDate ?? item.toDate ?? null,
      status: item.status ?? item.leaveStatus ?? "-",
      appliedOn: item.appliedOn ?? item.applied_on ?? null,
      approvedBy: item.approvedBy ?? item.approved_by ?? "-",
      remarks: item.remarks ?? item.reason ?? "-",
      leaveCategory: item.leaveCategory ?? item.leave_category ?? "-",
      holidayOverlap: !!item.holidayOverlap,
      leaveBalance: item.leaveBalance ?? item.leave_balance ?? 0,
    }));
  }, [apiRawList, departmentIdToNameMap]);

  const apiDepartmentsOptions = useMemo(() => {
    const list = departmentsData?.data ?? departmentsData ?? [];
    const baseOpts = Array.isArray(list)
      ? list.map((d: any) => ({
          value: String(d._id ?? d.id ?? ""),
          label: d.name ?? d.title ?? "",
        }))
      : [];

    const seen = new Set<string>();
    const unique = baseOpts.filter((o) => {
      if (!o.value) return false;
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });

    if (disabledDept && user?.department?._id) {
      const myId = String(user.department._id);
      if (!seen.has(myId)) {
        const myLabel =
          user.department?.name ??
          departmentIdToNameMap[myId] ??
          "My Department";
        unique.unshift({ value: myId, label: myLabel });
      }
    }

    return [{ value: "all", label: "All departments" }, ...unique];
  }, [departmentsData, disabledDept, user, departmentIdToNameMap]);

  const serverPages = serverPagination?.pages;
  const pageCount =
    serverPages ??
    Math.max(
      1,
      Math.ceil((serverPagination?.total ?? normalizedList.length) / pageSize)
    );

  const locallyFiltered = useMemo(() => {
    let list = [...normalizedList];

    return list;
  }, [normalizedList]);

  const paged =
    serverPagination && apiRawList && apiRawList.length > 0
      ? locallyFiltered
      : locallyFiltered.slice((page - 1) * pageSize, page * pageSize);

  const rows = paged.map((item: any) => {
    const initials = (item.employeeName || "U")
      .split(/\s+/)
      .map((n: string) => (n ? n[0] : ""))
      .slice(0, 2)
      .join("")
      .toUpperCase();
    return (
      <tr key={item._id}>
        <td>
          <div className={classes.employeeCell}>
            <Avatar size={40} radius={40}>
              {initials}
            </Avatar>
            <div className={classes.empMeta}>
              <div className={classes.empName}>{item.employeeName}</div>
              {/* <div className={classes.empRole}>{item.employeeId}</div> */}
            </div>
          </div>
        </td>
        <td>
          <Text size="sm">{item.department || "-"}</Text>
        </td>
        <td>
          <Text size="sm">{item.designation || "-"}</Text>
        </td>
        <td>
          <Group spacing={6} align="center">
            <Text size="sm" style={{ textTransform: "capitalize" }}>
              {item.leaveType || "-"}
            </Text>
          </Group>
        </td>
        <td>
          <Text size="sm">{formatDate(item.fromDate)}</Text>
        </td>
        <td>
          <Text size="sm">{formatDate(item.toDate)}</Text>
        </td>
        <td>
          <Text size="sm" weight={600}>
            {computeDays(item.fromDate, item.toDate)}
          </Text>
        </td>
        <td>
          <Badge
            color={statusColor(item.status)}
            variant="filled"
            sx={{ textTransform: "capitalize" }}
          >
            {item.status || "unknown"}
          </Badge>
        </td>
        <td>
          <Text size="sm">{formatDate(item.appliedOn)}</Text>
        </td>
        <td>
          <Text size="sm">{item.approvedBy || "-"}</Text>
        </td>

        <td>
          <Text size="sm">{item.leaveCategory || "-"}</Text>
        </td>
        <td>
          <Text size="sm">{item.holidayOverlap ? "Yes" : "No"}</Text>
        </td>
        <td>
          <Text size="sm">{item.leaveBalance}</Text>
        </td>
        <td>
          <Tooltip
            label={item?.remarks || "-"}
            withArrow
            position="top"
            color="dark"
          >
            <Text size="sm">
              {item.remarks ? textOverlap(item?.remarks, 10) : "-"}
            </Text>
          </Tooltip>
        </td>
        <td>
          <Group spacing={4} noWrap>
            <PermissionActionIcon
              permission="employees.employeeleaveEdit"
              aria-label="Edit leave"
              color="blue"
              variant="subtle"
              onClick={() => setEditRow(item)}
            >
              <IconEdit size={16} />
            </PermissionActionIcon>
            <PermissionActionIcon
              permission="employees.employeeleaveEdit"
              aria-label="Delete leave"
              color="red"
              variant="subtle"
              onClick={() => setDeleteId(item._id)}
            >
              <IconTrash size={16} />
            </PermissionActionIcon>
          </Group>
        </td>
      </tr>
    );
  });

  return (
    <Card shadow="sm" radius="md" p="md">
      {editRow && (
        <EditLeaveModal
          opened={!!editRow}
          onClose={() => {
            setEditRow(null);
            refetch();
          }}
          leaveId={editRow._id}
          initialData={{
            fromDate: editRow.fromDate,
            toDate: editRow.toDate,
            leaveType: editRow.leaveType,
            reason: editRow.remarks,
          }}
        />
      )}
      <DeleteApprovedLeaveModal
        opened={!!deleteId}
        onClose={() => {
          setDeleteId(null);
          refetch();
        }}
        leaveId={deleteId || ""}
      />
      <div className={classes.header}>
        <div>
          <Text className={classes.title}>Leave Report</Text>
          <Text size="xs" color="dimmed">
            Showing{" "}
            {serverPagination
              ? serverPagination.total ?? paged.length
              : paged.length}{" "}
            entries
          </Text>
        </div>

        <Group position="apart" align="center" style={{ marginBottom: 8 }}>
          <div className={classes.miniControls}>
            {/* <Text size="sm" className={classes.smallMuted}>
              Rows
            </Text>
            <Select
              data={["10", "25", "50"]}
              value={rowsPerPage}
              onChange={(v) => {
                setRowsPerPage(v || "10");
                setPage(1);
              }}
              style={{ width: 80 }}
            /> */}
            <Group grow>
              <Select
                label="Department"
                data={apiDepartmentsOptions}
                value={departmentId}
                onChange={(v) => {
                  if (!disabledDept) {
                    setDepartmentId(v || "all");
                    setPage(1);
                  }
                }}
                placeholder="Select department"
                rightSection={<IconChevronDown size={14} />}
                nothingFound="No departments"
                disabled={disabledDept}
              />

              <DateRangePicker
                label="Date Range"
                placeholder="Filter by date range"
                value={dateRange}
                onChange={(v) => {
                  setDateRange(v as [Date | null, Date | null]);
                  setPage(1);
                }}
                clearable
              />

              <Select
                label="Status"
                data={[
                  { value: "all", label: "All status" },
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                value={status}
                onChange={(v) => {
                  setStatus(v || "all");
                  setPage(1);
                }}
                rightSection={<IconChevronDown size={14} />}
              />

              <TextInput
                label="Search"
                placeholder="Search by name"
                icon={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                  setPage(1);
                }}
              />
            </Group>
          </div>
        </Group>
      </div>

      <div className={classes.tableWrap}>
        <Table verticalSpacing="md" highlightOnHover className={classes.table}>
          <thead style={{ backgroundColor: "#f5f5f5" }}>
            <tr>
              {/* <th>
                <Checkbox />
              </th> */}
              <th>Employee Name</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Leave Type</th>
              <th>Leave Start</th>
              <th>Leave End</th>
              <th>Total Days</th>
              <th>Status</th>
              <th>Applied On</th>
              <th>Approved By</th>

              <th>Category</th>
              <th>Overlap</th>
              <th>Balance</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={16}>
                  <Center style={{ padding: 20 }}>
                    <Text color="red">Error loading leave reports.</Text>
                  </Center>
                </td>
              </tr>
            ) : isLoading ? (
              <tr>
                <td colSpan={16}>
                  <Center style={{ padding: 20 }}>
                    <Loader />
                  </Center>
                </td>
              </tr>
            ) : paged.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={16}>
                  <Center>No leave requests found.</Center>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className={classes.footer}>
        <Text className={classes.smallMuted}>
          Showing {paged.length === 0 ? 0 : (page - 1) * pageSize + 1} -{" "}
          {Math.min(
            page * pageSize,
            serverPagination?.total ?? normalizedList.length
          )}{" "}
          of {serverPagination?.total ?? normalizedList.length}
        </Text>
        <Group spacing="xs">
          <Pagination
            total={pageCount}
            radius="sm"
            siblings={1}
            page={page}
            onChange={(p) => setPage(p)}
          />
        </Group>
      </div>
    </Card>
  );
}
